import { ColourScore, ColourScoreWithPreference } from 'api';

type Key = keyof ColourScore;
export interface ColourSortableRow {
  colourOrderPref: readonly Key[];
  prefCount: number;
  fullName: string;
}
const COLOUR_ORDER: readonly Key[] = ['blue', 'green', 'yellow', 'red'];
const colourPriority = new Map<Key, number>(
  COLOUR_ORDER.map((colour, index) => [colour, index]),
);
const LOWEST_PRIORITY_FALLBACK = Number.MAX_SAFE_INTEGER;
const getColourPriority = (colour: Key): number =>
  colourPriority.get(colour) ?? LOWEST_PRIORITY_FALLBACK;

export const buildColourOrder = (
  scoreArray: ColourScoreWithPreference,
): Key[] =>
  COLOUR_ORDER.map((colour) => ({
    colour,
    value: scoreArray[colour],
  }))
    .sort(
      (a, b) =>
        b.value - a.value ||
        getColourPriority(a.colour) - getColourPriority(b.colour),
    )
    .map(({ colour }) => colour);

export const compareByColourDominance =
  (direction: -1 | 1, isConsciousScore: boolean) =>
  (a: ColourSortableRow, b: ColourSortableRow): number => {
    const firstDifferenceIndex = a.colourOrderPref.findIndex(
      (colour, index) => colour !== b.colourOrderPref[index],
    );

    if (firstDifferenceIndex !== -1) {
      return (
        (getColourPriority(a.colourOrderPref[firstDifferenceIndex]) -
          getColourPriority(b.colourOrderPref[firstDifferenceIndex])) *
        direction
      );
    }

    if (a.prefCount !== b.prefCount) {
      return isConsciousScore
        ? (a.prefCount - b.prefCount) * direction
        : (b.prefCount - a.prefCount) * direction;
    }

    return a.fullName.localeCompare(b.fullName);
  };

export type FormattedHeader = {
  line1: string;
  line2?: string;
};
export const WIDTH_BUFFER_RATIO = 1.05;
export const BASE_TABLE_WIDTH = 195;

const LINE_SPLIT_RATIO = 0.7;
const MIN_REMAINDER_LENGTH = 3;

export const getInitialSplit = (text: string, maxChars: number): number =>
  text.lastIndexOf(' ', maxChars) > 0
    ? text.lastIndexOf(' ', maxChars)
    : Math.max(
        maxChars - MIN_REMAINDER_LENGTH,
        Math.floor(maxChars * LINE_SPLIT_RATIO),
      );

export const adjustSplitForRemainderSize = (
  text: string,
  split: number,
): number => {
  const remainder = text.slice(split).trim();

  return remainder.length <= MIN_REMAINDER_LENGTH &&
    split > MIN_REMAINDER_LENGTH
    ? text.lastIndexOf(' ', split - 1) || split
    : split;
};

export const splitText = (text: string, maxChars: number): FormattedHeader => {
  const split = adjustSplitForRemainderSize(
    text,
    getInitialSplit(text, maxChars),
  );

  const line1 = text.slice(0, split).trim();
  const remainder = text.slice(split).trim();

  const line2 =
    remainder.length > maxChars
      ? remainder.slice(0, maxChars - 1) + '…'
      : remainder || undefined;

  return { line1, line2 };
};
